import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getApiUrl, API_CONFIG } from "../config/api";
import { UniversalProductInfoResponse } from "../types";
import ImageSlider from "../components/ImageSlider";
// import RatingChart from "../components/RatingChart"; // больше не используется
import ProductReviews from "../components/ProductReviews";
import { PlatformIcon } from "../components/PlatformIcons";

interface UniversalProductDetailProps {}

const UniversalProductDetail: React.FC<UniversalProductDetailProps> = () => {
  const { platform, idType, value } = useParams<{
    platform: string;
    idType: string;
    value: string;
  }>();
  const { storeId, productId } = useParams<{
    storeId: string;
    productId: string;
  }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productInfo, setProductInfo] =
    useState<UniversalProductInfoResponse | null>(null);
  const [productInfoWildberries, setProductInfoWildberries] =
    useState<UniversalProductInfoResponse | null>(null);
  const [ozonDescription, setOzonDescription] = useState<string | null>(null);
  const [ratingStats, setRatingStats] = useState<{
    averageRating: number;
    totalReviews: number;
    ratingDistribution: Record<number, number>;
  } | null>(null);
  const [analysisData, setAnalysisData] = useState<{
    mainAdvantage?: { title: string; details?: string } | null;
    mainProblem?: { title: string; details?: string } | null;
    advantages?: string[];
    disadvantages?: string[];
    lastAnalyzedAt?: string | null;
  } | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const analysisKeyRef = useRef<string | null>(null);
  const [isDescExpanded, setIsDescExpanded] = useState<boolean>(false);
  const [headerPlatform, setHeaderPlatform] = useState<string | null>(null);
  const [headerStoreName, setHeaderStoreName] = useState<string | null>(null);

  // Убраны моки и дополнительные характеристики

  // Период для блока распределения рейтингов
  const ratingSinceDays = 90;

  useEffect(() => {
    const fetchProductInfo = async () => {
      const legacyValue = value;
      const newStoreId = storeId;
      const newProductId = productId;
      if (!legacyValue && (!newStoreId || !newProductId)) {
        setError("Идентификатор товара не указан");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        let data: any | null = null;
        if (newStoreId && newProductId) {
          // Новый маршрут: для Яндекс используем offer-mappings по offerIds
          // Для прочих платформ оставляем существующую ручку
          // Сначала выясним платформу магазина
          let platformOfStore: string | null = null;
          try {
            const storesResp = await axios.get(
              getApiUrl(API_CONFIG.ENDPOINTS.STORES),
              { headers }
            );
            const storesArr = Array.isArray(storesResp.data)
              ? storesResp.data
              : [];
            const storeObj = storesArr.find((s: any) => s.id === newStoreId);
            platformOfStore = storeObj
              ? String(storeObj.platform || "").toLowerCase()
              : null;
          } catch {
            platformOfStore = null;
          }

          if (platformOfStore === "yandex") {
            const mappings = await axios.post(
              `${getApiUrl("/api/products/by-store/mappings")}?store=${encodeURIComponent(newStoreId)}`,
              { offerIds: [String(newProductId)], language: "RU" },
              { headers }
            );
            const items = (mappings.data?.items || []) as any[];
            data = items[0]?.offer || null;
          } else {
            const resp = await axios.get<UniversalProductInfoResponse>(
              `${getApiUrl("/api/products")}/${encodeURIComponent(newProductId)}?store=${encodeURIComponent(newStoreId)}`,
              { headers }
            );
            data = resp.data;
          }
        } else {
          // Легаси маршрут
          const resp = await axios.get<UniversalProductInfoResponse>(
            `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_INFO)}/${legacyValue}` +
              (platform
                ? `?platform=${encodeURIComponent(platform)}&idType=${encodeURIComponent(idType || "offer")}`
                : ""),
            { headers }
          );
          data = resp.data;
        }

        if (platform === "ozon" && idType === "offer") {
          const { data } = await axios.get<UniversalProductInfoResponse>(
            `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_INFO)}/${value}?platform=wildberries`,
            { headers }
          );

          setProductInfoWildberries(data);
        }

        setProductInfo(data);
      } catch (err) {
        const axiosErr: any = err as any;
        const serverMessage = axiosErr?.response?.data?.message;
        const errorMessage =
          typeof serverMessage === "string"
            ? serverMessage
            : err instanceof Error
              ? err.message
              : "Неизвестная ошибка";
        setError(errorMessage);
        console.error("Ошибка при загрузке информации о товаре:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductInfo();
  }, [platform, idType, value, storeId, productId]);

  // Определяем платформу и название магазина для заголовка
  useEffect(() => {
    const resolveHeaderInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        } as Record<string, string>;

        if (storeId) {
          // Загружаем список магазинов и ищем нужный
          const resp = await axios.get(getApiUrl(API_CONFIG.ENDPOINTS.STORES), {
            headers,
          });
          const stores = Array.isArray(resp.data) ? resp.data : [];
          const store = stores.find((s: any) => s.id === storeId);
          if (store) {
            setHeaderPlatform(String(store.platform || "").toLowerCase());
            setHeaderStoreName(store.name || null);
            return;
          }
        }

        // Фоллбек по имеющимся данным
        const p = (platform || productInfo?.platform || "").toString();
        setHeaderPlatform(p ? p.toLowerCase() : null);
        setHeaderStoreName(null);
      } catch {
        const p = (platform || productInfo?.platform || "").toString();
        setHeaderPlatform(p ? p.toLowerCase() : null);
        setHeaderStoreName(null);
      }
    };
    resolveHeaderInfo();
  }, [storeId, platform, productInfo?.platform]);

  // Доп. запрос за описанием для OZON при новом маршруте
  useEffect(() => {
    const fetchOzonDescription = async () => {
      if (!storeId || !productId) {
        setOzonDescription(null);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        } as Record<string, string>;
        // Попробуем получить описание — если магазин не OZON, вернется message
        const resp = await axios.get(
          `${getApiUrl("/api/products")}/${encodeURIComponent(productId)}/description?store=${encodeURIComponent(storeId)}`,
          { headers }
        );
        const desc = (resp.data as any)?.description;
        if (typeof desc === "string" && desc.trim().length > 0) {
          setOzonDescription(desc);
        } else if ((resp.data as any)?.result?.description) {
          setOzonDescription((resp.data as any).result.description);
        } else {
          setOzonDescription(null);
        }
      } catch {
        setOzonDescription(null);
      }
    };
    fetchOzonDescription();
  }, [storeId, productId]);

  // Загружаем реальную статистику рейтингов
  useEffect(() => {
    const fetchRatingStats = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        } as Record<string, string>;

        const resolvedPlatform = (
          (headerPlatform || platform || productInfo?.platform || "ozon") + ""
        ).toLowerCase();

        // Wildberries: считаем статистику с помощью WB ручки, как для отзывов, но с большим take и датами
        if (resolvedPlatform === "wildberries" && storeId && productId) {
          const now = Date.now();
          const since = now - ratingSinceDays * 24 * 60 * 60 * 1000;
          const wbParams = new URLSearchParams();
          wbParams.append("take", "5000");
          wbParams.append("skip", "0");
          wbParams.append("order", "dateDesc");
          wbParams.append("storeId", storeId);
          wbParams.append("nmId", String(Number(productId)));
          wbParams.append("dateFrom", String(since));
          wbParams.append("dateTo", String(now));
          const { data } = await axios.get(
            `${getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS)}?${wbParams}`,
            { headers }
          );
          const feedbacks: any[] = (
            data && data.data && Array.isArray(data.data.feedbacks)
              ? data.data.feedbacks
              : []
          ) as any[];
          const distribution: Record<number, number> = {
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0,
          };
          let total = 0;
          let sum = 0;
          for (const fb of feedbacks) {
            const r = Number(fb?.productValuation);
            if (Number.isFinite(r) && r >= 1 && r <= 5) {
              distribution[r] = (distribution[r] || 0) + 1;
              total += 1;
              sum += r;
            }
          }
          const average = total > 0 ? sum / total : 0;
          setRatingStats({
            averageRating: Number(average.toFixed(2)),
            totalReviews: total,
            ratingDistribution: distribution,
          });
          return;
        }

        // Яндекс: временно не считаем рейтинг и не запрашиваем отзывы
        if (resolvedPlatform === "yandex" && storeId && productId) {
          setRatingStats(null);
          return;
        }

        // OZON и прочие: используем наш SKU-ориентированный эндпоинт (последний месяц)
        const skuSource: any = productInfo?.sku;
        const parsedSku =
          typeof skuSource === "number"
            ? skuSource
            : typeof skuSource === "string" && skuSource.trim() !== ""
              ? Number(skuSource)
              : NaN;
        if (!Number.isFinite(parsedSku)) return;

        const params = new URLSearchParams();
        params.append("sku", String(parsedSku));
        params.append("sinceDays", String(ratingSinceDays));
        const endpoint =
          API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_RATING_STATS_RECENT;
        const { data } = await axios.get(`${getApiUrl(endpoint)}?${params}`, {
          headers,
        });
        if (
          data &&
          typeof data.averageRating === "number" &&
          typeof data.totalReviews === "number" &&
          data.ratingDistribution
        ) {
          setRatingStats({
            averageRating: data.averageRating,
            totalReviews: data.totalReviews,
            ratingDistribution: data.ratingDistribution,
          });
        }
      } catch (e) {
        console.warn("Не удалось загрузить статистику рейтингов", e);
      }
    };

    fetchRatingStats();
  }, [
    productInfo?.sku,
    headerPlatform,
    platform,
    storeId,
    productId,
    productInfo?.platform,
  ]);

  // Загружаем аналитику отзывов (ИИ) с бэка
  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setAnalysisError(null);
        setAnalysisLoading(true);
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        } as Record<string, string>;

        // Универсальная сборка параметров для анализа: поддержка обоих маршрутов
        const params = new URLSearchParams();
        let effectiveIdType: "offer" | "id" | null = null;
        let effectivePlatform: string | null = null;

        if (storeId && productId) {
          if (!headerPlatform) {
            // ждём, пока определится платформа магазина, чтобы не дублировать запросы
            setAnalysisLoading(false);
            return;
          }
          effectivePlatform =
            (headerPlatform || platform || productInfo?.platform || "ozon") +
            "";
          const platformLower = String(effectivePlatform).toLowerCase();
          if (platformLower === "yandex") {
            // Временно отключаем анализ для Яндекс
            setAnalysisLoading(false);
            setAnalysisData(null);
            return;
          }
          // Для Яндекс анализ выполняем по offerId, для остальных — по id
          if (platformLower === "yandex") {
            effectiveIdType = "offer";
            params.append("idType", "offer");
            params.append("offerId", String(productId));
          } else {
            effectiveIdType = "id";
            params.append("idType", "id");
            params.append("id", String(productId));
          }
          params.append("platform", platformLower);
        } else if (value) {
          const idKind = (idType || (platform ? "id" : "offer")).toLowerCase();
          effectiveIdType = idKind as any;
          effectivePlatform = platform || productInfo?.platform || null;
          params.append("idType", idKind);
          if (idKind === "offer") {
            params.append("offerId", value);
          } else {
            params.append("id", value);
            if (platform) params.append("platform", platform);
          }
        }

        if (!effectiveIdType) {
          return;
        }

        // Идемпотентность: если уже дергали анализ с теми же параметрами — выходим
        const currentKey =
          storeId && productId
            ? `store:${storeId}:${productId}:${String(effectivePlatform || "").toLowerCase()}`
            : `legacy:${String(platform || "").toLowerCase()}:${String(idType || "").toLowerCase()}:${String(value || "")}`;
        if (analysisKeyRef.current === currentKey) {
          setAnalysisLoading(false);
          return;
        }
        analysisKeyRef.current = currentKey;

        const { data } = await axios.get(
          `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_ANALYSIS)}?${params}`,
          { headers }
        );

        // Обработка ошибки GPT: если массивы преимуществ/недостатков null — считаем как ошибку связи
        const isGptError =
          data?.analysis &&
          data.analysis.advantages === null &&
          data.analysis.disadvantages === null;

        if (isGptError) {
          setAnalysisError("Не удалось выполнить анализ (нет связи с GPT)");
        }

        setAnalysisData({
          mainAdvantage: data?.mainAdvantage || null,
          mainProblem: data?.mainProblem || null,
          advantages:
            Array.isArray(data?.analysis?.advantages) && !isGptError
              ? data.analysis.advantages
              : [],
          disadvantages:
            Array.isArray(data?.analysis?.disadvantages) && !isGptError
              ? data.analysis.disadvantages
              : [],
          lastAnalyzedAt: data?.lastAnalyzedAt || null,
        });

        // При открытии страницы: если после последнего анализа появилось >=3 новых текстовых отзыва за 90 дней — перегенерировать
        const lastAnalyzedAtIso: string | null = data?.lastAnalyzedAt || null;
        const sinceDays = 90;
        if (lastAnalyzedAtIso) {
          const reviewsParams = new URLSearchParams();
          reviewsParams.append("sinceDays", String(sinceDays));
          reviewsParams.append("idType", effectiveIdType);
          if (effectiveIdType === "offer") {
            const offerId = params.get("offerId") || value || "";
            if (offerId) reviewsParams.append("offerId", offerId);
          } else {
            const pid =
              storeId && productId
                ? String(productId)
                : params.get("id") || value || "";
            if (pid) reviewsParams.append("id", pid);
            const pf =
              storeId && productId
                ? String(effectivePlatform || "ozon").toLowerCase()
                : params.get("platform") || String(effectivePlatform || "");
            if (pf) reviewsParams.append("platform", String(pf).toLowerCase());
          }

          try {
            // Для Яндекс не загружаем отзывы для проверки необходимости перегенерации
            const pfLower = (
              (storeId && productId
                ? String(effectivePlatform || "")
                : params.get("platform") || String(effectivePlatform || "")) +
              ""
            ).toLowerCase();
            if (pfLower === "yandex") {
              return;
            }
            const { data: reviewsRes } = await axios.get(
              `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_REVIEWS)}?${reviewsParams.toString()}`,
              { headers }
            );
            const lastAtMs = Date.parse(lastAnalyzedAtIso);
            const reviews: any[] = Array.isArray(reviewsRes?.reviews)
              ? reviewsRes.reviews
              : [];
            const textNewCount = reviews.filter((r: any) => {
              const text = r?.reviewText || r?.text || "";
              if (!text || String(text).trim() === "") return false;
              const dateRaw =
                r?.publishedAt || r?.published_at || r?.createdAt || null;
              const d = dateRaw ? new Date(dateRaw) : null;
              const ms = d ? d.getTime() : NaN;
              return Number.isFinite(ms) && ms > lastAtMs;
            }).length;

            if (textNewCount >= 3) {
              const forceParams = new URLSearchParams(params.toString());
              forceParams.append("force", "true");
              const { data: forced } = await axios.get(
                `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_ANALYSIS)}?${forceParams}`,
                { headers }
              );

              const forcedGptError =
                forced?.analysis &&
                forced.analysis.advantages === null &&
                forced.analysis.disadvantages === null;
              if (forcedGptError) {
                setAnalysisError(
                  "Не удалось выполнить анализ (нет связи с GPT)"
                );
              } else {
                setAnalysisError(null);
              }

              setAnalysisData({
                mainAdvantage: forced?.mainAdvantage || null,
                mainProblem: forced?.mainProblem || null,
                advantages:
                  Array.isArray(forced?.analysis?.advantages) && !forcedGptError
                    ? forced.analysis.advantages
                    : [],
                disadvantages:
                  Array.isArray(forced?.analysis?.disadvantages) &&
                  !forcedGptError
                    ? forced.analysis.disadvantages
                    : [],
                lastAnalyzedAt: forced?.lastAnalyzedAt || null,
              });
            }
          } catch (e) {
            // не критично для загрузки страницы
          }
        }
      } catch (e) {
        console.warn("Не удалось загрузить аналитику товара", e);
        setAnalysisError("Ошибка загрузки анализа товара");
      } finally {
        setAnalysisLoading(false);
      }
    };
    fetchAnalysis();
  }, [
    value,
    idType,
    platform,
    storeId,
    productId,
    headerPlatform,
    productInfo?.platform,
  ]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Ошибка загрузки
              </h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => navigate("/reviews")}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Вернуться к отзывам
          </button>
        </div>
      </div>
    );
  }

  // --------- Derived ---------
  const displayName =
    productInfo?.name || (productInfo as any)?.title || "Товар";
  // Для Wildberries приходят фото в поле photos с различными размерами — используем square
  const wbPhotos = ((productInfo as any)?.photos || []) as Array<any>;
  const wbSquareImages = Array.isArray(wbPhotos)
    ? wbPhotos
        .map(
          (p) =>
            p?.square || p?.hq || p?.big || p?.c516x688 || p?.c246x328 || p?.tm
        )
        .filter((u: any): u is string => typeof u === "string" && u.length > 0)
    : [];
  // Яндекс: картинки приходят в offer.pictures
  const yandexPictures = Array.isArray((productInfo as any)?.pictures)
    ? (((productInfo as any)?.pictures as any[]).filter(
        (u) => typeof u === "string" && u.length > 0
      ) as string[])
    : [];
  const displayImages = (
    wbSquareImages.length > 0
      ? wbSquareImages
      : yandexPictures.length > 0
        ? yandexPictures
        : productInfo?.images || []
  ) as string[];
  const rawPrice = productInfo?.price as string | number | undefined;
  let priceNumber: number | null = null;
  if (typeof rawPrice === "number") {
    priceNumber = rawPrice;
  } else if (typeof rawPrice === "string") {
    const normalized = rawPrice
      .replace(/\s/g, "")
      .replace(/,/g, ".")
      .replace(/[^0-9.]/g, "");
    const parsed = parseFloat(normalized);
    priceNumber = isNaN(parsed) ? null : parsed;
  }
  const displayRating = ratingStats?.averageRating ?? 0;
  const displayReviewCount = ratingStats?.totalReviews ?? 0;
  const ratingDistribution = ratingStats?.ratingDistribution || null;

  // Статический объект (вне хуков), чтобы не нарушать правила хуков
  const analysisMainAdvantage = analysisData?.mainAdvantage || null;
  const analysisMainProblem = analysisData?.mainProblem || null;
  const additionalAdvantagesList =
    analysisData?.advantages && analysisData.advantages.length > 1
      ? analysisData.advantages.slice(1, 5).map((t: string) => ({
          title: t,
          details: "",
        }))
      : [];
  const additionalProblemsList =
    analysisData?.disadvantages && analysisData.disadvantages.length > 1
      ? analysisData.disadvantages.slice(1, 5).map((t: string) => ({
          title: t,
          details: "",
        }))
      : [];
  const hasAnalysis = Boolean(
    analysisMainAdvantage?.title ||
      analysisMainProblem?.title ||
      (analysisData?.advantages && analysisData.advantages.length > 0) ||
      (analysisData?.disadvantages && analysisData.disadvantages.length > 0)
  );

  const resolvedPlatformForReviews = (
    (headerPlatform || platform || productInfo?.platform || "ozon") + ""
  ).toLowerCase();

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Навигация */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/reviews")}
          className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Вернуться к отзывам
        </button>
      </div>

      {/* Верхний блок: слайдер слева, название/цена/рейтинг справа */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
        <div className="lg:col-span-4">
          {displayImages.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
              <ImageSlider images={displayImages} productName={displayName} />
            </div>
          )}
        </div>
        <div className="lg:col-span-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 h-full">
            <div className="h-full flex flex-col gap-4">
              {/* Верх: платформа/магазин, название и тех.инфо */}
              <div className="h-full flex flex-col">
                {(headerPlatform || headerStoreName) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    {headerPlatform && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200 capitalize">
                        <PlatformIcon
                          platform={headerPlatform || ""}
                          className="w-4 h-4 mr-1"
                        />
                        {headerPlatform}
                      </span>
                    )}
                    {headerStoreName &&
                      (storeId ? (
                        <Link
                          to={`/stores/${storeId}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                          title={headerStoreName}
                        >
                          {headerStoreName}
                        </Link>
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200"
                          title={headerStoreName}
                        >
                          {headerStoreName}
                        </span>
                      ))}
                  </div>
                )}
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  {displayName}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  {productInfo?.id && (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-gray-500">ID:</span>
                      <span className="font-medium">{productInfo.id}</span>
                    </span>
                  )}
                  {productInfo?.offer_id && (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-gray-500">Offer ID:</span>
                      <span className="font-medium">
                        {productInfo.offer_id}
                      </span>
                    </span>
                  )}
                  {productInfo?.sku && (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-gray-500">SKU:</span>
                      <span className="font-medium">{productInfo.sku}</span>
                    </span>
                  )}
                </div>
                {(ozonDescription ||
                  productInfo?.description ||
                  productInfoWildberries?.description) &&
                  (() => {
                    const rawDesc = (ozonDescription ||
                      productInfo?.description ||
                      productInfoWildberries?.description ||
                      "") as string;
                    const normalizedDesc = rawDesc.replace(
                      /<br\s*\/?>(\r?\n)?/gi,
                      "\n"
                    );
                    return (
                      <div className="mt-2">
                        <div
                          className="text-sm text-gray-600 whitespace-pre-line transition-[max-height] duration-300 ease-in-out"
                          style={{
                            maxHeight: isDescExpanded ? "none" : "8rem",
                            overflow: isDescExpanded ? "visible" : "hidden",
                          }}
                        >
                          {normalizedDesc}
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsDescExpanded((v) => !v)}
                          className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          {isDescExpanded ? "Свернуть" : "Показать полностью"}
                        </button>
                      </div>
                    );
                  })()}
                <div className="mt-auto" />
              </div>

              {/* Низ: слева цена, справа рейтинг/звезды/кол-во отзывов */}
              <div className="mt-auto flex items-end justify-between">
                <div className="text-left">
                  {priceNumber !== null ? (
                    <div className="text-3xl font-extrabold text-gray-900 tabular-nums whitespace-nowrap leading-none">
                      {priceNumber.toLocaleString("ru-RU")} ₽
                    </div>
                  ) : (
                    typeof rawPrice === "string" &&
                    rawPrice.trim() !== "" && (
                      <div className="text-3xl font-extrabold text-gray-900 tabular-nums whitespace-nowrap leading-none">
                        {rawPrice}
                      </div>
                    )
                  )}
                </div>
                {resolvedPlatformForReviews !== "yandex" && (
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-1 text-yellow-400 text-xl">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          className={
                            i < Math.round(displayRating) ? "" : "text-gray-300"
                          }
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      {displayRating.toFixed(1)}
                      <span className="text-gray-400 font-normal"> · </span>
                      <span className="text-gray-600">
                        {displayReviewCount} отзывов
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Анализ + компактное распределение рейтингов в одной строке */}
      {resolvedPlatformForReviews !== "yandex" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8 items-stretch">
          {/* Анализ отзывов слева (75%) */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 h-full">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Анализ товара на основе отзывов
                  </h3>
                  <button
                    onClick={async () => {
                      try {
                        setAnalysisError(null);
                        setAnalysisLoading(true);
                        const token = localStorage.getItem("token");
                        const headers = {
                          "Content-Type": "application/json",
                          ...(token && { Authorization: `Bearer ${token}` }),
                        } as Record<string, string>;

                        const buildParams = (): URLSearchParams => {
                          const params = new URLSearchParams();
                          if (storeId && productId) {
                            params.append("idType", "id");
                            params.append("id", String(productId));
                            const pf =
                              (headerPlatform ||
                                platform ||
                                productInfo?.platform ||
                                "ozon") + "";
                            params.append("platform", String(pf).toLowerCase());
                            return params;
                          }
                          const idKind = (
                            idType || (platform ? "id" : "offer")
                          ).toLowerCase();
                          params.append("idType", idKind);
                          if (idKind === "offer") {
                            if (value) params.append("offerId", value);
                          } else {
                            if (value) params.append("id", value);
                            if (platform) params.append("platform", platform);
                          }
                          return params;
                        };

                        const params = buildParams();
                        params.append("force", "true");

                        const { data } = await axios.get(
                          `${getApiUrl(API_CONFIG.ENDPOINTS.UNIVERSAL_PRODUCT_ANALYSIS)}?${params.toString()}`,
                          { headers }
                        );

                        const isGptError =
                          data?.analysis &&
                          data.analysis.advantages === null &&
                          data.analysis.disadvantages === null;
                        if (isGptError) {
                          setAnalysisError(
                            "Не удалось выполнить анализ (нет связи с GPT)"
                          );
                        }

                        setAnalysisData({
                          mainAdvantage: data?.mainAdvantage || null,
                          mainProblem: data?.mainProblem || null,
                          advantages:
                            Array.isArray(data?.analysis?.advantages) &&
                            !isGptError
                              ? data.analysis.advantages
                              : [],
                          disadvantages:
                            Array.isArray(data?.analysis?.disadvantages) &&
                            !isGptError
                              ? data.analysis.disadvantages
                              : [],
                          lastAnalyzedAt: data?.lastAnalyzedAt || null,
                        });
                      } catch (e) {
                        console.warn(
                          "Не удалось перегенерировать аналитику",
                          e
                        );
                        setAnalysisError("Ошибка при перегенерации анализа");
                      } finally {
                        setAnalysisLoading(false);
                      }
                    }}
                    className="group inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 hover:border-gray-300 bg-white text-gray-600 hover:text-gray-900 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={analysisLoading}
                    title="Перегенерировать аналитику"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="w-[18px] h-[18px]"
                    >
                      <path d="M21 12a9 9 0 10-3.51 7.07" />
                      <path d="M21 3v7h-7" />
                    </svg>
                  </button>
                </div>
                <p className="text-gray-600 mt-1">
                  Ключевые преимущества и проблемные зоны
                </p>
              </div>

              {analysisLoading ? (
                <div className="flex justify-center py-10">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                </div>
              ) : hasAnalysis ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {analysisError && (
                    <div className="lg:col-span-2">
                      <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                        {analysisError}
                      </div>
                    </div>
                  )}
                  {/* Преимущества */}
                  <div>
                    <div className="mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <h4 className="text-lg font-semibold text-gray-900">
                        Конкурентные преимущества
                      </h4>
                    </div>

                    {/* Главное преимущество */}
                    {analysisMainAdvantage?.title && (
                      <div className="mb-4 p-4 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="inline-flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-600 text-white font-semibold">
                                Главное
                              </span>
                              <span className="text-emerald-700 text-sm font-medium">
                                Высокий приоритет
                              </span>
                            </div>
                            <div className="text-gray-900 font-semibold">
                              {analysisMainAdvantage.title}
                            </div>
                            {analysisMainAdvantage.details && (
                              <div className="text-gray-700 text-sm mt-1">
                                {analysisMainAdvantage.details}
                              </div>
                            )}
                          </div>
                          <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700">
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Доп преимущества */}
                    {additionalAdvantagesList.length > 0 && (
                      <ul className="space-y-3">
                        {additionalAdvantagesList.map((adv, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-emerald-200 transition-colors"
                          >
                            <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                              <svg
                                className="w-3.5 h-3.5"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-gray-900 text-sm font-medium">
                                {adv.title}
                              </div>
                              {adv.details && (
                                <div className="text-gray-600 text-sm">
                                  {adv.details}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Проблемные зоны */}
                  <div>
                    <div className="mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-rose-500" />
                      <h4 className="text-lg font-semibold text-gray-900">
                        Проблемные зоны
                      </h4>
                    </div>

                    {/* Главная проблема */}
                    {analysisMainProblem?.title && (
                      <div className="mb-4 p-4 rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 to-red-50">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="inline-flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 text-xs rounded-full bg-rose-600 text-white font-semibold">
                                Главная
                              </span>
                              <span className="text-rose-700 text-sm font-medium">
                                Критично
                              </span>
                            </div>
                            <div className="text-gray-900 font-semibold">
                              {analysisMainProblem.title}
                            </div>
                            {analysisMainProblem.details && (
                              <div className="text-gray-700 text-sm mt-1">
                                {analysisMainProblem.details}
                              </div>
                            )}
                          </div>
                          <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-rose-100 text-rose-700">
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13l-1.41 1.41L12 13.41l-3.59 3.59L7 15l3.59-3.59L7 7.83 8.41 6.41 12 10l3.59-3.59L17 7.83l-3.59 3.59L17 15z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Доп проблемы */}
                    {additionalProblemsList.length > 0 && (
                      <ul className="space-y-3">
                        {additionalProblemsList.map((pr, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-rose-200 transition-colors"
                          >
                            <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                              <svg
                                className="w-3.5 h-3.5"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13l-1.41 1.41L12 13.41l-3.59 3.59L7 15l3.59-3.59L7 7.83 8.41 6.41 12 10l3.59-3.59L17 7.83l-3.59 3.59L17 15z" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-gray-900 text-sm font-medium">
                                {pr.title}
                              </div>
                              {pr.details && (
                                <div className="text-gray-600 text-sm">
                                  {pr.details}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="mb-4">Товар не проанализирован</div>
                  <div className="text-sm">
                    Нажмите кнопку с иконкой, чтобы запустить анализ
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Компактное распределение рейтингов справа (25%) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 h-full flex flex-col">
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Распределение рейтингов
                </h3>
                <p className="text-xs text-gray-500">
                  Сводка по оценкам покупателей за последние {ratingSinceDays}{" "}
                  дней
                </p>
              </div>

              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = ratingDistribution
                    ? (ratingDistribution as any)[rating] || 0
                    : 0;
                  const percentage =
                    displayReviewCount > 0
                      ? (count / displayReviewCount) * 100
                      : 0;
                  return (
                    <div key={rating} className="">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600">{rating}★</span>
                        <span className="text-gray-900 font-medium">
                          {Math.round(percentage)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${rating >= 4 ? "bg-green-500" : rating === 3 ? "bg-yellow-400" : "bg-red-400"}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-auto pt-3 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900">
                  {displayRating.toFixed(1)} / 5
                </div>
                <div className="text-xs text-gray-500">
                  {displayReviewCount} отзывов
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Блок отзывов */}
      {resolvedPlatformForReviews !== "yandex" && (
        <div className="mt-10">
          {(value || (storeId && productId)) && (
            <ProductReviews
              offer_id={idType === "offer" ? value : undefined}
              productId={
                idType === "id"
                  ? Number(value)
                  : storeId && productId
                    ? Number(productId)
                    : undefined
              }
              platform={resolvedPlatformForReviews}
              sku={productInfo?.sku}
              productName={displayName}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UniversalProductDetail;
